import React, { useContext, useState } from 'react'
import { usePopper } from 'react-popper'

import { Placement } from '@popperjs/core'

import { EngagementScoreFlames } from 'components/EngagementScoreFlames'
import { ExplainHoverCard } from 'components/Explain'
import FadeInAnimation from 'components/FadeInAnimation'
import Portal from 'components/Portal'
import { CurrentUserContext } from 'context/currentUserContext'
import { useDebouncedCallback } from 'hooks/useDebouncedCallback'
import { AccountActivity } from 'modules/accountActivity/domain'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

const EngagementScore = ({
    accountActivity,
    boundaryRef,
    placement,
    showMore
  }: {
    accountActivity: AccountActivity
    boundaryRef: HTMLDivElement | null
    placement?: Placement
    showMore?: boolean
  }) => {
    const [isShown, setIsShown] = useState(false)
    const currentUser = useContext(CurrentUserContext)
    const debouncedMouseEvent = useDebouncedCallback((toggle: boolean) => {
      setIsShown(toggle)
    }, 100)
  
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
    const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
      placement: placement ?? 'left-end',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [200, 15]
          }
        },
        {
          name: 'preventOverflow',
          options: {
            rootBoundary: 'document',
            boundary: boundaryRef ?? 'clippingParents',
            mainAxis: true,
            altAxis: false,
            padding: 0,
            altBoundary: true,
            tether: false
          }
        }
      ]
    })
    return (
      <div
        onMouseEnter={() => {
          const hoverEvent = accountActivity.accountScoredActivity
            ? TrackType.AI_ENGAGEMENT_SCORE_TOOLTIP_HOVER
            : TrackType.ENGAGEMENT_SCORE_TOOLTIP_HOVER
          setIsShown(true)
          debouncedMouseEvent(true)
          segmentTrack(hoverEvent, {
            orgId: currentUser?.orgId,
            accountId: accountActivity.accountId,
            userId: currentUser?.id
          })
        }}
        onMouseLeave={() => debouncedMouseEvent(false)}>
        <div ref={setReferenceElement}>
          {accountActivity && (
            <EngagementScoreFlames score={accountActivity.normalizedEngagementScore} />
          )}
        </div>
        {isShown && (
          <FadeInAnimation inProp={isShown}>
            <Portal>
              <div
                className={styles.popper}
                ref={setPopperElement}
                style={popperStyles.popper}
                {...attributes.popper}>
                <ExplainHoverCard accountActivity={accountActivity} limit={3} showMore={showMore} />
              </div>
            </Portal>
          </FadeInAnimation>
        )}
      </div>
    )
  }

  export default EngagementScore